import React from 'react';

/**
 * React Error Boundary — catches render crashes in child components
 * and shows a themed fallback UI instead of a blank screen.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('⛳ ErrorBoundary caught:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
          <div className="text-center max-w-md mx-auto">
            {/* Broken flag illustration */}
            <div className="relative inline-flex items-center justify-center mb-8">
              <div
                className="absolute -inset-10 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(239,68,68,0.1), transparent 70%)',
                  filter: 'blur(20px)',
                }}
              />
              <div
                className="relative w-24 h-24 rounded-full flex items-center justify-center"
                style={{
                  background: 'linear-gradient(145deg, rgba(239,68,68,0.08), rgba(239,68,68,0.02))',
                  border: '1.5px solid rgba(239,68,68,0.15)',
                  boxShadow: '0 0 40px rgba(239,68,68,0.08)',
                }}
              >
                <span className="text-5xl select-none">💥</span>
              </div>
            </div>

            <h1 className="text-2xl font-bold text-white/90 mb-3">
              Something broke on the course
            </h1>

            <p className="text-sm text-white/40 font-light leading-relaxed mb-6 max-w-xs mx-auto">
              A component hit a hazard and crashed. Don't worry — your game data is safe.
            </p>

            {/* Error details (dev only) */}
            {this.state.error && (
              <div
                className="mb-6 p-3 rounded-lg text-left overflow-auto max-h-32"
                style={{
                  background: 'rgba(239,68,68,0.06)',
                  border: '1px solid rgba(239,68,68,0.12)',
                }}
              >
                <p className="text-[10px] font-mono text-red-400/60 uppercase tracking-wider mb-1">Error Details</p>
                <code className="text-xs font-mono text-red-300/70 break-all">
                  {this.state.error.message || 'Unknown error'}
                </code>
              </div>
            )}

            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 hover:scale-[1.02] cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.06))',
                border: '1px solid rgba(34,197,94,0.25)',
                color: 'rgba(34,197,94,0.9)',
                boxShadow: '0 4px 16px rgba(34,197,94,0.1)',
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Return to Clubhouse
            </button>

            <p className="mt-8 text-[10px] font-mono text-white/15 tracking-wider uppercase">
              Ground under repair · Free drop available
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
